import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { PagingType, UTable } from '@components/common/UTable/UTable';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { useGetMeQuery } from '@/api/auth/api';
import {
  useGetDictionariesQuery,
  useLazyGetDictionaryQuery,
} from '@/api/dictionaries/api';
import { Dictionary } from '@/api/dictionaries/types';
import { useGetOrganizationsShortQuery } from '@/api/organizations/api';
import {
  useCreateRegistryRecordMutation,
  useGetRegistryRecordsQuery,
  useGetUterusQuery,
  useUpdateRegistryRecordMutation,
} from '@/api/vaccination/registry/api';
import { IndividualShort } from '@/api/vaccination/registry/types';
import { ARCHIVE, REGISTRY, VACCIANTION } from '@/constants/routes';
import {
  addSchema,
  addWithEventsSchema,
} from '@/pages/Vaccination/components/registry/card/add.config';
import { editSchema } from '@/pages/Vaccination/components/registry/card/edit.config';
import { columns } from '@/pages/Vaccination/components/registry/columns.config';
import { filterSchema } from '@/pages/Vaccination/components/registry/filters.config';
import { Sort } from '@/shared/api/types';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { hasPermission } from '@/shared/security/hasPermission';
import { vaccination } from '@/shared/security/permissions/vaccination';
import { IndividualStatus } from '@/shared/vaccination/enums';
import { AppDispatch, RootState } from '@/store';
import { setVaccinationRegistryFilters } from '@/store/filtersSlice';
import { setVaccinationRegistrySort } from '@/store/sortSlice';
import { mapTableIndexWithPage } from '@/utils/utils';

export const VaccinationRegistry = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();

  const filters: Record<string, any> = useSelector(
    (state: RootState) => state.filtersSlice.vaccinationRegistry,
  );
  const setFilters = (filters: Record<string, any>) =>
    dispatch(setVaccinationRegistryFilters(filters));
  const defaultSort: Sort[] = [
    {
      key: 'birthDate',
      order: 'desc',
    },
  ];
  const sort: Sort[] | undefined = useSelector(
    (state: RootState) => state.sorterSlice.vaccinationRegistry,
  );
  const setSort = (sorter: Sort[] | undefined) =>
    dispatch(setVaccinationRegistrySort(sorter));
  const onSortChange = (sort?: Sort[]) => {
    setSort(sort);
  };
  const [paging, setPaging] = useState<PagingType>({ page: 0, size: 10 });

  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isAddWithEventsModalOpen, setIsAddWithEventsModalOpen] =
    useState<boolean>(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editModalData, setEditModalData] = useState<Record<string, any>>({});

  const { data: user } = useGetMeQuery();
  const { data: dictionaries } = useGetDictionariesQuery();

  const organizationId =
    user?.organization?.level === OrganizationLevelType.ORGANIZATION
      ? user?.organization?.id
      : user?.organization?.level === OrganizationLevelType.STRUCTURAL_DIVISION
        ? user?.organization?.parentId
        : undefined;

  const [eventsRequest, setEventsRequest] = useState<any>();
  const [getEvents, { reset: resetEvents }] = useLazyGetDictionaryQuery();
  const getCompletedEvents = (birthDate: string) => {
    if (!birthDate) {
      resetEvents();
      return;
    }

    const birth = dayjs(birthDate, 'DD.MM.YYYY').startOf('day');
    const today = dayjs().startOf('day');
    const age = today.diff(birth, 'day');

    const request = {
      dictionaryName: Dictionary.EVENT_PROTOCOL,
      filters: {
        organizationId: organizationId,
        endAgeFrom: age,
      },
    };

    setEventsRequest(request);
    getEvents(request);
  };

  const { data: records, isFetching } = useGetRegistryRecordsQuery({
    filters,
    paging,
    sort,
  });
  const [createRecord] = useCreateRegistryRecordMutation();
  const [updateRecord] = useUpdateRegistryRecordMutation();

  const { data: uterus } = useGetUterusQuery(undefined, {
    skip: !hasPermission(user.auth, {
      permissions: vaccination.individual.addPassport,
      level: [
        OrganizationLevelType.ORGANIZATION,
        OrganizationLevelType.STRUCTURAL_DIVISION,
      ],
    }),
  });

  const { data: organizations } = useGetOrganizationsShortQuery({
    level: OrganizationLevelType.ORGANIZATION,
  });
  const { data: allStructurals } = useGetOrganizationsShortQuery({
    level: OrganizationLevelType.STRUCTURAL_DIVISION,
  });
  const { data: structurals } = useGetOrganizationsShortQuery(
    {
      level: OrganizationLevelType.STRUCTURAL_DIVISION,
      parentId: user?.organization?.id,
    },
    { skip: !user?.organization?.id },
  );

  const filterTitle = useMemo(() => {
    if (filters.organizationId && organizations) {
      const org = organizations.filter((item) => item.id === filters.organizationId);
      return `Хозяйство: ${org[0].name}`;
    }
    return null;
  }, [filters, organizations]);

  return (
    <>
      <PageTitle title={'Журнал'} />
      <UTable
        size={'small'}
        data={mapTableIndexWithPage(records?.content, paging)}
        columns={columns(dictionaries)}
        currentSort={sort}
        defaultSort={defaultSort}
        onChange={onSortChange}
        loading={isFetching}
        paging={paging}
        total={records?.totalElements}
        onPagingChange={setPaging}
        buttons={[
          {
            addBtn: true,
            action: () => setIsAddModalOpen(true),
            security: {
              permissions: vaccination.individual.addPassport,
              level: [
                OrganizationLevelType.ORGANIZATION,
                OrganizationLevelType.STRUCTURAL_DIVISION,
              ],
            },
          },
          {
            text: 'Добавить с мероприятиями',
            type: 'primary',
            action: () => setIsAddWithEventsModalOpen(true),
            // Ввод выполненных мероприятий при регистрации — по праву пользователя
            security: {
              permissions: [
                vaccination.individual.addPassport,
                vaccination.individual.importHistory,
              ],
              strategy: 'all',
              level: [
                OrganizationLevelType.ORGANIZATION,
                OrganizationLevelType.STRUCTURAL_DIVISION,
              ],
            },
          },
          {
            text: 'Архив',
            type: 'primary',
            action: () => navigate(VACCIANTION + REGISTRY + ARCHIVE),
            security: {
              permissions: vaccination.individual.viewListArchive,
            },
          },
        ]}
        filter={{
          filters,
          schema: filterSchema(dictionaries, organizations),
          onFilter: (filters) => setFilters(filters),
          onReset: () => setFilters({}),
          underTitle: filterTitle,
          security: {
            permissions: vaccination.individual.viewListFiltering,
          },
        }}
        actions={() => [
          {
            icon: <EyeOutlined />,
            action: (record: IndividualShort) =>
              navigate(VACCIANTION + REGISTRY + `/${record?.id}`),
            security: {
              permissions: vaccination.individual.viewPassport,
            },
          },
          {
            icon: <EditOutlined />,
            action: (record: IndividualShort) => {
              setEditModalData(record);
              setIsEditModalOpen(true);
            },
            security: {
              permissions: vaccination.individual.editPassport,
              level: [
                OrganizationLevelType.ORGANIZATION,
                OrganizationLevelType.STRUCTURAL_DIVISION,
              ],
            },
          },
        ]}
      />

      <Modal
        open={isAddModalOpen}
        mode={'form'}
        formKey={'addModal'}
        title={'Добавить особь'}
        centered={true}
        data={{ organizationId: user?.organization?.name }}
        schema={addSchema(user?.organization?.level, dictionaries, uterus, structurals)}
        onSubmit={async (record: IndividualShort) => {
          const { organizationId, completedEvents, ...rest } = record;
          void organizationId;
          void completedEvents;
          await createRecord({
            ...rest,
            structuralDivisionId:
              user?.organization?.level === OrganizationLevelType.ORGANIZATION
                ? rest.structuralDivisionId
                : user?.organization?.id,
            diseaseDescription:
              rest?.individualStatus === IndividualStatus.SICK
                ? rest.diseaseDescription
                : null,
            deadRemovalDate: [IndividualStatus.DEAD, IndividualStatus.REMOVAL].includes(
              rest?.individualStatus,
            )
              ? rest.deadRemovalDate
              : null,
          }).unwrap();
          setIsAddModalOpen(false);
        }}
        onCancel={() => setIsAddModalOpen(false)}
        width={'375px'}
        layout={{
          formLayout: 'vertical',
          itemLayout: 'vertical',
        }}
        destroyOnHidden={true}
      />
      <Modal
        open={isAddWithEventsModalOpen}
        mode={'form'}
        formKey={'addModal'}
        title={'Добавить особь'}
        centered={true}
        data={{ organizationId: user?.organization?.name }}
        schema={addWithEventsSchema(
          user?.organization?.level,
          getCompletedEvents,
          eventsRequest,
          dictionaries,
          uterus,
          structurals,
        )}
        onSubmit={async (record: IndividualShort) => {
          const { organizationId, ...rest } = record;
          void organizationId;

          await createRecord({
            ...rest,
            structuralDivisionId:
              user?.organization?.level === OrganizationLevelType.ORGANIZATION
                ? rest.structuralDivisionId
                : user?.organization?.id,
            diseaseDescription:
              rest?.individualStatus === IndividualStatus.SICK
                ? rest.diseaseDescription
                : null,
            deadRemovalDate: [IndividualStatus.DEAD, IndividualStatus.REMOVAL].includes(
              rest?.individualStatus,
            )
              ? rest.deadRemovalDate
              : null,
          }).unwrap();
          setIsAddWithEventsModalOpen(false);
        }}
        onCancel={() => {
          resetEvents();
          setIsAddWithEventsModalOpen(false);
        }}
        width={'555px'}
        layout={{
          formLayout: 'vertical',
          itemLayout: 'vertical',
        }}
        destroyOnHidden={true}
      />
      <Modal
        open={isEditModalOpen}
        mode={'form'}
        formKey={'editModal'}
        title={'Редактировать особь'}
        centered={true}
        data={editModalData}
        schema={editSchema(dictionaries, organizations, allStructurals)}
        onSubmit={async (record: IndividualShort) => {
          await updateRecord({
            ...record,
            diseaseDescription:
              record?.individualStatus === IndividualStatus.SICK
                ? record.diseaseDescription
                : null,
            deadRemovalDate: [IndividualStatus.DEAD, IndividualStatus.REMOVAL].includes(
              record?.individualStatus,
            )
              ? record.deadRemovalDate
              : null,
            id: editModalData.id,
          }).unwrap();
          setEditModalData({});
          setIsEditModalOpen(false);
        }}
        onCancel={() => {
          setIsEditModalOpen(false);
        }}
        width={'375px'}
        layout={{
          formLayout: 'vertical',
          itemLayout: 'vertical',
        }}
        destroyOnHidden={true}
      />
    </>
  );
};
